import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/gallery`).then((r) => setImages(r.data)).catch(() => setImages([]));
  }, []);

  const goTo = useCallback((index) => {
    if (isAnimating || images.length === 0) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating, images.length]);

  const next = useCallback(() => goTo((current + 1) % images.length), [current, goTo, images.length]);
  const prev = useCallback(() => goTo((current - 1 + images.length) % images.length), [current, goTo, images.length]);

  useEffect(() => {
    if (paused || images.length === 0) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next, paused, images.length]);

  if (images.length === 0) return null;

  return (
    <section data-testid="gallery-section" className="py-16 md:py-24" style={{ backgroundColor: "#0A2540" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>Life at Trident</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Our Academy Gallery</h2>
          <p className="text-blue-300 text-sm mt-2">A glimpse into the vibrant life at Trident Academy</p>
        </div>

        <div className="relative rounded-2xl overflow-hidden shadow-2xl"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="gallery-slider">
          <div className="relative" style={{ height: "480px" }}>
            {images.map((img, i) => (
              <img key={i} src={img.src} alt={img.alt}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: current === i ? 1 : 0, pointerEvents: current === i ? "auto" : "none" }} />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-white font-semibold text-base drop-shadow-lg">{images[current]?.caption}</p>
              <span className="text-xs text-white/60">{current + 1} / {images.length}</span>
            </div>
            <button onClick={prev} data-testid="gallery-prev-btn" aria-label="Previous"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-all backdrop-blur-sm">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} data-testid="gallery-next-btn" aria-label="Next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-all backdrop-blur-sm">
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex justify-center items-center gap-2 py-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            {images.map((_, i) => (
              <button key={i} onClick={() => goTo(i)} data-testid={`gallery-dot-${i}`}
                className="transition-all duration-300 rounded-full"
                style={{ width: current === i ? "28px" : "8px", height: "8px", backgroundColor: current === i ? "#F2C94C" : "rgba(255,255,255,0.4)" }} />
            ))}
          </div>
        </div>

        <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <button key={i} onClick={() => goTo(i)} data-testid={`gallery-thumb-${i}`}
              className="flex-shrink-0 rounded-xl overflow-hidden transition-all duration-300"
              style={{ width: "100px", height: "68px", border: current === i ? "3px solid #F2C94C" : "3px solid transparent", opacity: current === i ? 1 : 0.6 }}>
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        <div className="text-center mt-8">
          <a href="https://www.instagram.com/thetridentacademy" target="_blank" rel="noopener noreferrer"
            data-testid="gallery-instagram-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 text-white"
            style={{ background: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
            <Instagram size={18} />
            Follow us on Instagram @thetridentacademy
          </a>
        </div>
      </div>
    </section>
  );
}
